import React, { useState } from 'react';
import { useContact } from '../../context/ContactContext';
import { displaySummary } from '../../lib/summary';
import { linkLabel, linkKind } from '../../lib/contactIds';
import { isV4 } from '../../lib/uiFlag';

// Console v4 (row 234): ONE caller card replaces the v3 pair (EscalationContext
// + HandoverCard). The v3 pair were fixed-height and scrolled inside, so on a
// 1600x960 screen the AI summary, the last question and answer and the intents
// were clipped out of view (Storm's live demo check, 2026-10-31).
//
// Caller sentiment stays in the assist rail directly above Coaching (vault
// #222, enforced by verify-v3-oncall).
//
// v4 order: who (name, plan, at most two tags) -> AI summary
// folded to two lines -> one toggle for everything else (escalation reason,
// last question/answer, call details, intents). Short by default, so the
// transcript keeps its room and nothing is half-clipped. Same fields as v3;
// nothing new is read.

const TRIGGER_LABEL = {
  'keyword': 'Asked for a person',
  'low-confidence': 'No KB answer',
  'specialist-offer': 'AI offered a specialist',
  'no-input': 'Caller not heard',
  'turn-cap': 'Turn limit',
  'system-error': 'AI error',
  'guardrail': 'Safety check',
};

export default function CallerCardV4() {
  const { display } = useContact();
  const [open, setOpen] = useState(false);
  if (!display || !isV4()) return null;

  const { customer, meta, aiHandled, intents = [], aiSummary } = display;
  const esc = display.escalation ?? {};
  const brand = esc.brandName ?? display.brandName ?? null;
  const fromAi = Boolean(esc.reason || esc.lastQuestion || esc.lastAnswer || esc.handoverQuestion);
  const wanted = esc.handoverQuestion && esc.handoverQuestion !== esc.lastQuestion ? esc.handoverQuestion : null;
  const summary = displaySummary(aiSummary);
  const trigger = esc.trigger ? (TRIGGER_LABEL[esc.trigger] ?? esc.trigger) : null;

  return (
    <section className="v4-caller" aria-label="Caller">
      <div className="v4-caller-row">
        <h1 className="v4-caller-name">{customer.name ?? customer.phone ?? 'Caller'}</h1>
        {customer.plan && <span className="v4-muted">{customer.plan}</span>}
        {(fromAi || aiHandled) && <span className="v4-pill v4-pill-teal">From AI</span>}
        {trigger && <span className="v4-pill v4-pill-amber">{trigger}</span>}
        {brand && <span className="v4-muted v4-caller-brand">{brand}</span>}
      </div>

      <p className={`v4-summary${open ? '' : ' v4-fold'}`}>
        {summary ?? 'No AI summary for this call.'}
      </p>

      <button type="button" className="v4-link" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {open ? 'Hide details' : 'Full summary and call details'}
      </button>

      {open && (
        <div className="v4-details">
          {esc.reason && (
            <div className="v4-detail"><div className="v4-label">Why the AI handed over</div><div>{esc.reason}</div></div>
          )}
          {wanted && (
            <div className="v4-detail"><div className="v4-label">What the caller wanted</div><div>{wanted}</div></div>
          )}
          {esc.lastQuestion && (
            <div className="v4-detail"><div className="v4-label">Last question</div><div>{esc.lastQuestion}</div></div>
          )}
          {esc.lastAnswer && (
            <div className="v4-detail"><div className="v4-label">Last AI answer</div><div>{esc.lastAnswer}</div></div>
          )}
          <dl className="v4-meta">
            <div>
              <dt>Contact ID</dt>
              <dd className="v4-mono" title={meta.contactId}>{meta.contactId}</dd>
              {meta.initialContactId && meta.initialContactId !== meta.contactId && (
                <dd className="v4-muted v4-small">
                  {linkLabel(meta.initiationMethod)} · {linkKind(meta.initiationMethod) === 'transfer' ? 'from' : 'chain root'} {String(meta.initialContactId).slice(0, 8)}…
                </dd>
              )}
            </div>
            <div><dt>Queue</dt><dd>{meta.queue}</dd></div>
            <div><dt>Started</dt><dd className="v4-mono">{meta.startTime}</dd></div>
            <div><dt>Dialled</dt><dd className="v4-mono">{meta.dnis}</dd></div>
            {customer.account && <div><dt>Account</dt><dd className="v4-mono">{customer.account}</dd></div>}
          </dl>
          {intents.length > 0 && (
            <div className="v4-intents">
              {intents.map(i => <span key={i} className="v4-pill">{i}</span>)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
